// Pure merge logic for the consolidated Agents list (fn-72.3 / R3-R5).
//
// The Agents page surfaces TWO backing queries:
//   - `agents.list` (the resilient SPINE): every deployed/global agent row,
//     each tagged `source: 'config_repo' | 'console'`.
//   - `configRepoAgents.list` (the ENRICHMENT): the rich config-repo view with
//     deploy controls, skill/MCP dependency status, scan metadata. This query
//     can be pending or error — it is NOT load-bearing for "don't lose a row".
//
// **Split by `source`, never by name** (Codex Major from plan review): a
// `source='console'` agent NEVER appears in the config-repo query, so it must
// be rendered from `agents.list`. A `source='config_repo'` agent.list row is
// normally REPRESENTED by the rich config-repo list (with full controls), so it
// is NOT rendered a second time from `agents.list` on the success path. There
// is NO name-based dedupe — a console agent and a config-repo agent may share a
// name, and name-dedupe would silently hide one real row.
//
// **Degraded fallback** (Codex Major; memory `bug/ui/secondary-query-joins-
// must-branch-on-2026-06-19`): when the config-repo query is pending or errors,
// the `source='config_repo'` agent.list rows are rendered as BASIC fallback
// rows (name link + start + star, no deploy/dependency controls) so deployed
// config-repo agents never vanish into a confident empty list. These fallback
// rows are suppressed the moment the config-repo query succeeds (it then owns
// those rows with full controls) — no double render.
//
// **Favorites** (R5): every row carries a stable `favoriteKey`:
//   - `agent:<id>` for rows that have a platform agent id (deployed — both
//     console-source and config-repo with `agent_id`),
//   - `config-repo:<name>` for not-yet-deployed config-repo entries
//     (`agent_id === null`).
// Favorited rows pin to the TOP via a STABLE partition (favorites first,
// original order preserved within each group), applied across BOTH groups.

/** The slice of an `agents.list` row this module needs (the resilient spine). */
/**
 * ADR 0043 — the agent's memory attachment map (from `GET /agents`'
 * `agent.memory`, surfaced verbatim on the `agents.list` spine): which scopes
 * the agent mounts and at what access mode. Undefined when the agent declares
 * no memory. `firm` is always `read_only` (write is deploy-rejected). Read-only
 * / informational — drives the agents-list memory badge.
 */
export interface AgentMemoryStatus {
  readonly user?: 'read_only' | 'read_write';
  readonly firm?: 'read_only';
  readonly matter?: 'read_only' | 'read_write';
}

export interface AgentListSpineRow {
  readonly id: string;
  readonly name: string;
  readonly source: 'config_repo' | 'console';
  /** ADR 0043 — the agent's memory attachment map (informational). */
  readonly memory?: AgentMemoryStatus;
  /**
   * fn-75 / ADR 0035 — soft-archive flag from the BFF `AgentRow`. Only ever
   * `true` for a `source='console'` agent (config-repo agents are not
   * archivable), and only present in the spine when the list query opted in
   * with `includeArchived: true`. Drives the greyed archived-row rendering +
   * the Unarchive affordance.
   */
  readonly archived: boolean;
}

/**
 * The favorite-key convention. `agent:<id>` for anything with a platform agent
 * id; `config-repo:<name>` for a never-deployed config-repo entry. Centralized
 * here so the row builders and any caller derive the SAME key.
 */
export function favoriteKeyForAgentId(id: string): string {
  return `agent:${id}`;
}
export function favoriteKeyForConfigRepoName(name: string): string {
  return `config-repo:${name}`;
}

/**
 * A console-authored agent row (`agents.list` `source='console'`). Always
 * deployed (has an id) → clickable + startable + favoriteable.
 *
 * fn-75 — `archived` carries the soft-archive state through so the row renders
 * greyed with an Unarchive affordance (only surfaced when the caller opted into
 * `includeArchived`; otherwise the spine never contains archived rows).
 */
export interface ConsoleAgentEntry {
  readonly kind: 'console';
  readonly id: string;
  readonly name: string;
  readonly favoriteKey: string;
  readonly archived: boolean;
  /** ADR 0043 — the agent's memory attachment map (from the spine). */
  readonly memory?: AgentMemoryStatus;
}

/**
 * A basic fallback row for a deployed `config_repo` agent, rendered ONLY when
 * the config-repo enrichment query is unavailable (pending/error). Sourced from
 * the `agents.list` spine, so it always has an id. No deploy/dependency
 * controls — those need the config-repo query.
 */
export interface ConfigRepoFallbackEntry {
  readonly kind: 'config-repo-fallback';
  readonly id: string;
  readonly name: string;
  readonly favoriteKey: string;
  /** ADR 0043 — the agent's memory attachment map (from the spine). */
  readonly memory?: AgentMemoryStatus;
}

/**
 * A rich config-repo row (`configRepoAgents.list`), rendered on the success
 * path with ALL controls. `agentRow` is the verbatim config-repo row the
 * existing card renderer consumes; `favoriteKey` is derived from it.
 */
export interface ConfigRepoEntry<Row> {
  readonly kind: 'config-repo';
  readonly name: string;
  readonly favoriteKey: string;
  readonly agentRow: Row;
  /**
   * ADR 0043 — the agent's memory attachment map, JOINED from the `agents.list`
   * spine by agent id (the rich config-repo projection does NOT carry it).
   * Undefined for a never-deployed repo-only agent (no spine row) or an agent
   * that declares no memory.
   */
  readonly memory?: AgentMemoryStatus;
}

export type AgentEntry<Row> = ConsoleAgentEntry | ConfigRepoFallbackEntry | ConfigRepoEntry<Row>;

/** The minimal shape a config-repo row must expose for keying. */
interface ConfigRepoKeyable {
  readonly agent_id: string | null;
  readonly name: string;
}

/**
 * Derive a config-repo row's stable favoriteKey: `agent:<id>` when deployed,
 * `config-repo:<name>` otherwise. Exported so the merge + any direct caller
 * agree on the key.
 */
export function favoriteKeyForConfigRepoRow(row: ConfigRepoKeyable): string {
  return row.agent_id !== null
    ? favoriteKeyForAgentId(row.agent_id)
    : favoriteKeyForConfigRepoName(row.name);
}

export interface BuildAgentEntriesArgs<Row extends ConfigRepoKeyable> {
  /** All `agents.list` rows (the resilient spine). */
  readonly spineRows: readonly AgentListSpineRow[];
  /** The current config-repo page rows (`configRepoAgents.list.data`). */
  readonly configRepoRows: readonly Row[];
  /**
   * Whether the config-repo query SUCCEEDED. When `false` (pending or error),
   * the config-repo rows are not trusted and deployed `config_repo` spine rows
   * are rendered as fallback rows instead.
   */
  readonly configRepoOk: boolean;
  /** The current favorite-key set (membership test for the pin-to-top sort). */
  readonly favorites: ReadonlySet<string>;
}

/**
 * Build the ordered render model for the consolidated Agents list.
 *
 * Order before the favorites sort:
 *   1. console-source rows (from the spine), in spine order,
 *   2. then EITHER the rich config-repo rows (success), in config-repo order,
 *      OR the `config_repo` fallback rows (degraded), in spine order.
 *
 * Then a STABLE favorites-first partition is applied across the whole list:
 * favorited rows keep their relative order at the front, non-favorited keep
 * theirs after. No name dedupe; a `config_repo` row is never double-rendered
 * (either the rich row OR the fallback row, never both).
 */
export function buildAgentEntries<Row extends ConfigRepoKeyable>(
  args: BuildAgentEntriesArgs<Row>,
): AgentEntry<Row>[] {
  // ADR 0043 — the memory attachment map lives ONLY on the `agents.list` spine
  // (from `GET /agents`); the rich config-repo projection doesn't carry it. Key
  // it by agent id so every entry kind can join its declared memory.
  const memoryById = new Map<string, AgentMemoryStatus>();
  for (const row of args.spineRows) {
    if (row.memory !== undefined) memoryById.set(row.id, row.memory);
  }

  const consoleEntries: ConsoleAgentEntry[] = args.spineRows
    .filter((row) => row.source === 'console')
    .map((row) => ({
      kind: 'console',
      id: row.id,
      name: row.name,
      favoriteKey: favoriteKeyForAgentId(row.id),
      archived: row.archived,
      ...(row.memory !== undefined ? { memory: row.memory } : {}),
    }));

  let configRepoSection: AgentEntry<Row>[];
  if (args.configRepoOk) {
    // Success: the rich config-repo list owns every config_repo row. Join its
    // memory from the spine by deployed agent id (undefined for repo-only rows).
    configRepoSection = args.configRepoRows.map((row) => {
      const memory = row.agent_id !== null ? memoryById.get(row.agent_id) : undefined;
      return {
        kind: 'config-repo',
        name: row.name,
        favoriteKey: favoriteKeyForConfigRepoRow(row),
        agentRow: row,
        ...(memory !== undefined ? { memory } : {}),
      };
    });
  } else {
    // Degraded: render the deployed config_repo spine rows as basic fallbacks
    // so they never vanish. (Never-deployed repo-only agents have no spine row,
    // so they are simply absent until the config-repo query recovers — which
    // the inline notice tells the operator.)
    configRepoSection = args.spineRows
      .filter((row) => row.source === 'config_repo')
      .map((row) => ({
        kind: 'config-repo-fallback',
        id: row.id,
        name: row.name,
        favoriteKey: favoriteKeyForAgentId(row.id),
        ...(row.memory !== undefined ? { memory: row.memory } : {}),
      }));
  }

  const ordered: AgentEntry<Row>[] = [...consoleEntries, ...configRepoSection];
  return sortFavoritesFirst(ordered, args.favorites);
}

/**
 * Which empty-state message the Agents list should show when no rows render.
 *   - `none`           — there are rows; render the list.
 *   - `no-agents`      — genuinely no agents exist; show the "ask an admin" copy.
 *   - `no-filter-match`— agents EXIST but the current config-repo filter/page
 *                        excludes them; show the no-match copy, NOT "no agents".
 *
 * The `agents.list` spine is unfiltered and fetched at the max page size (the
 * caller surfaces a truncation notice past that), so `spineCount === 0` is the
 * authoritative "no deployed agents exist" signal. When the spine is empty
 * but a config-repo filter is active, undeployed config-repo entries may exist
 * and simply be filtered out — that is `no-filter-match`, not `no-agents`
 * (Codex impl-review Major: filtered-empty must not claim "No agents available").
 */
export type AgentsEmptyState = 'none' | 'no-agents' | 'no-filter-match';

export function agentsEmptyState(args: {
  readonly agentEntriesCount: number;
  readonly spineCount: number;
  readonly hasActiveConfigRepoFilters: boolean;
}): AgentsEmptyState {
  if (args.agentEntriesCount > 0) return 'none';
  if (args.spineCount === 0 && !args.hasActiveConfigRepoFilters) return 'no-agents';
  return 'no-filter-match';
}

/**
 * Stable favorites-first partition: favorited rows move to the front keeping
 * their relative order; non-favorited stay after keeping theirs. A two-bucket
 * partition (not `Array.prototype.sort`) so it is provably stable regardless of
 * the engine's sort stability and never re-orders within a group.
 */
export function sortFavoritesFirst<Entry extends { readonly favoriteKey: string }>(
  entries: readonly Entry[],
  favorites: ReadonlySet<string>,
): Entry[] {
  const favored: Entry[] = [];
  const rest: Entry[] = [];
  for (const entry of entries) {
    if (favorites.has(entry.favoriteKey)) favored.push(entry);
    else rest.push(entry);
  }
  return [...favored, ...rest];
}
